import { cloneElement, forwardRef } from 'react';
import type {
  ButtonHTMLAttributes,
  HTMLAttributes,
  InputHTMLAttributes,
  ReactElement,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';
import { Link } from 'react-router-dom';
import type { LinkProps } from 'react-router-dom';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ControlSize = 'sm' | 'md' | 'lg';

function classes(...values: Array<string | false | null | undefined>): string {
  return values.filter(Boolean).join(' ');
}

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ControlSize;
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'secondary',
    size = 'md',
    loading = false,
    disabled,
    className,
    children,
    type = 'button',
    ...props
  },
  ref,
) {
  return (
    <button
      {...props}
      ref={ref}
      type={type}
      className={classes('ds-button', `ds-button--${variant}`, `ds-button--${size}`, className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
    >
      {loading ? <span className="ds-button__spinner" aria-hidden="true" /> : null}
      {children}
    </button>
  );
});

interface ActionLinkProps extends LinkProps {
  variant?: ButtonVariant;
  size?: ControlSize;
}

export function ActionLink({
  variant = 'secondary',
  size = 'md',
  className,
  children,
  ...props
}: ActionLinkProps): JSX.Element {
  return (
    <Link
      {...props}
      className={classes('ds-button', `ds-button--${variant}`, `ds-button--${size}`, className)}
    >
      {children}
    </Link>
  );
}

interface CardProps extends HTMLAttributes<HTMLDivElement> {
  tone?: 'default' | 'muted';
  elevation?: 'flat' | 'raised';
  padding?: 'none' | 'sm' | 'md' | 'lg';
}

export const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  { tone = 'default', elevation = 'flat', padding = 'md', className, ...props },
  ref,
) {
  return (
    <div
      {...props}
      ref={ref}
      className={classes(
        'ds-card',
        tone === 'muted' && 'ds-card--muted',
        elevation === 'raised' && 'ds-card--raised',
        padding !== 'none' && `ds-card--${padding}`,
        className,
      )}
    />
  );
});

interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: 'neutral' | 'accent' | 'success' | 'warning' | 'danger';
}

export function Badge({ tone = 'neutral', className, ...props }: BadgeProps): JSX.Element {
  return <span {...props} className={classes('ds-badge', `ds-badge--${tone}`, className)} />;
}

interface AlertProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  tone?: 'info' | 'success' | 'warning' | 'danger';
  title?: ReactNode;
}

export function Alert({
  tone = 'info',
  title,
  className,
  children,
  ...props
}: AlertProps): JSX.Element {
  return (
    <div {...props} className={classes('ds-alert', `ds-alert--${tone}`, className)}>
      {title ? <span className="ds-alert__title">{title}</span> : null}
      {children ? <div className="ds-alert__body">{children}</div> : null}
    </div>
  );
}

interface FieldProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  label: ReactNode;
  htmlFor: string;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  children: ReactElement<{
    'aria-describedby'?: string;
    'aria-invalid'?: boolean | 'false' | 'true';
    required?: boolean;
  }>;
}

export function Field({
  label,
  htmlFor,
  hint,
  error,
  required,
  className,
  children,
  ...props
}: FieldProps): JSX.Element {
  const descriptionId = hint || error ? `${htmlFor}-description` : undefined;
  const control = cloneElement(children, {
    'aria-describedby': children.props['aria-describedby'] ?? descriptionId,
    'aria-invalid': children.props['aria-invalid'] ?? (error ? true : undefined),
    required: children.props.required ?? required,
  });
  return (
    <div {...props} className={classes('ds-field', className)}>
      <label className="ds-field__label" htmlFor={htmlFor}>
        {label}
        {required ? (
          <span className="ds-field__required" aria-hidden="true">
            *
          </span>
        ) : null}
      </label>
      {control}
      {error ? (
        <p className="ds-field__error" id={descriptionId}>
          {error}
        </p>
      ) : hint ? (
        <p className="ds-field__hint" id={descriptionId}>
          {hint}
        </p>
      ) : null}
    </div>
  );
}

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { invalid, className, 'aria-invalid': ariaInvalid, ...props },
  ref,
) {
  return (
    <input
      {...props}
      ref={ref}
      className={classes('ds-control', className)}
      aria-invalid={invalid || ariaInvalid || undefined}
    />
  );
});

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  invalid?: boolean;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { invalid, className, 'aria-invalid': ariaInvalid, ...props },
  ref,
) {
  return (
    <select
      {...props}
      ref={ref}
      className={classes('ds-control', className)}
      aria-invalid={invalid || ariaInvalid || undefined}
    />
  );
});

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { invalid, className, 'aria-invalid': ariaInvalid, ...props },
  ref,
) {
  return (
    <textarea
      {...props}
      ref={ref}
      className={classes('ds-control', className)}
      aria-invalid={invalid || ariaInvalid || undefined}
    />
  );
});
